import { trackEvent } from 'user-analytics';

/**
 * Analytics event names for Doters flexible redemption feature.
 */
export const DOTERS_FLEXIBLE_EVENTS = Object.freeze({
  FLEXIBLE_NAME_CHANGED: 'Doters Flexible Name Changed',
  FLEXIBLE_TOGGLE_ON: 'Doters Flexible Toggle On',
  FLEXIBLE_TOGGLE_OFF: 'Doters Flexible Toggle Off',
  FLEXIBLE_MODAL_CONFIRMED: 'Doters Flexible Modal Confirmed',
  FLEXIBLE_MODAL_CANCELLED: 'Doters Flexible Modal Cancelled',
});

/**
 * Base tracker for loyalty wallet state changes.
 * @param {Object} params - Tracker parameters
 * @param {string} params.wallet - Wallet type (doters, costapass, siempreplus)
 * @param {string} params.purchaseToken - Purchase token identifier
 * @param {number} params.amount - Amount value
 * @param {string} params.action - Action performed (Apply, Remove, Modify)
 */
export const loyaltyTracker = ({ wallet, purchaseToken, amount, action }) => {
  trackEvent('Wallet State Changed', {
    'Wallet Type': wallet,
    'Amount': amount,
    'Purchase Token': purchaseToken,
    'Action': action,
  });
};

/**
 * Track when a loyalty wallet is applied to a purchase.
 * @param {Object} params - Tracker parameters
 * @param {string} params.wallet - Wallet type
 * @param {string} params.purchaseToken - Purchase token
 * @param {number} params.amount - Amount applied
 */
export const loyaltyApplyTracker = ({ wallet, purchaseToken, amount }) => {
  loyaltyTracker({ wallet, purchaseToken, amount, action: 'Apply' });
};

/**
 * Track when a loyalty wallet is removed from a purchase.
 * @param {Object} params - Tracker parameters
 * @param {string} params.wallet - Wallet type
 * @param {string} params.purchaseToken - Purchase token
 */
export const loyaltyRemoveTracker = ({ wallet, purchaseToken }) => {
  loyaltyTracker({ wallet, purchaseToken, amount: 0, action: 'Remove' });
};

/**
 * Track when a loyalty wallet amount is modified.
 * @param {Object} params - Tracker parameters
 * @param {string} params.wallet - Wallet type
 * @param {string} params.purchaseToken - Purchase token
 * @param {number} params.amount - New amount
 */
export const loyaltyModifyTracker = ({ wallet, purchaseToken, amount }) => {
  loyaltyTracker({ wallet, purchaseToken, amount, action: 'Modify' });
};

/**
 * Track when a user modifies passenger name in flexible mode
 * @param {Object} data - Analytics data
 * @param {number} data.passengerIndex - Index of the passenger
 * @param {string} data.fieldName - Field modified: 'firstName', 'lastName', 'secondLastName'
 * @param {string} data.value - New value of the field
 * @param {string} data.walletType - Wallet type (doters, siempreplus)
 * @param {boolean} [data.isCleared=false] - Whether field was cleared
 */
export const trackFlexibleNameChange = ({
  passengerIndex,
  fieldName,
  value,
  walletType,
  isCleared = false,
}) => {
  trackEvent(DOTERS_FLEXIBLE_EVENTS.FLEXIBLE_NAME_CHANGED, {
    passengerIndex,
    fieldModified: fieldName,
    walletType,
    fieldCleared: isCleared,
    valueLength: value?.length || 0,
  });
};

/**
 * Track when a user enables flexible editing mode for Doters guest purchase
 */
export const trackFlexibleToggleOn = () => {
  trackEvent(DOTERS_FLEXIBLE_EVENTS.FLEXIBLE_TOGGLE_ON);
};

/**
 * Track when a user disables flexible editing mode for Doters guest purchase
 */
export const trackFlexibleToggleOff = () => {
  trackEvent(DOTERS_FLEXIBLE_EVENTS.FLEXIBLE_TOGGLE_OFF);
};

/**
 * Track when a user confirms the flexible modal (accepts the switch)
 */
export const trackFlexibleModalConfirmed = () => {
  trackEvent(DOTERS_FLEXIBLE_EVENTS.FLEXIBLE_MODAL_CONFIRMED);
};

/**
 * Track when a user cancels the flexible modal (rejects the switch)
 */
export const trackFlexibleModalCancelled = () => {
  trackEvent(DOTERS_FLEXIBLE_EVENTS.FLEXIBLE_MODAL_CANCELLED);
};
